import { Dispatch, SetStateAction, useCallback } from "react"
import { Block, useStyletron } from "@mezo-org/mezo-clay"

import {
  selectCollateralAmount,
  selectLoanAmount,
  selectWalletBalances,
  useDappSelector,
} from "#/redux-state"
import CurrencySymbol from "#/shared/components/CurrencySymbol"
import { ENABLE_BORROW_LIQUIDATIONS_PRICES } from "#/shared/constants"
import {
  useBorrowInterestRate,
  useCollateralPrice,
  useLiquidationPrice,
  useLoanAmountWithFees,
  useNewLoanCollateralizationRatio,
} from "#/shared/hooks"
import {
  bigIntToHumanReadableFormat,
  floatToBigInt,
  separateThousandsWithComma,
} from "#/shared/utils"
import { ComponentStepProps } from "#/ui/Modal"

import {
  BorrowProgress,
  BorrowReviewBox,
  BorrowReviewListItem,
  BorrowTerms,
} from "../components"

import { BorrowReward } from "./components"

type BorrowReviewProps = ComponentStepProps & {
  handleBorrow: () => Promise<void>
  setHasAcceptedTerms: Dispatch<SetStateAction<boolean>>
  hasAcceptedTerms: boolean
}

export default function BorrowReview({
  onClickContinue,
  handleBorrow,
  hasAcceptedTerms,
  setHasAcceptedTerms,
}: BorrowReviewProps) {
  const [css, theme] = useStyletron()

  const { MUSD } = useDappSelector(selectWalletBalances)

  const loanAmount = useDappSelector(selectLoanAmount)
  const collateralAmount = useDappSelector(selectCollateralAmount)

  const collateralAmountValue = floatToBigInt(collateralAmount)

  const { collateralizationRatioLabel } = useNewLoanCollateralizationRatio(
    floatToBigInt(loanAmount),
    collateralAmountValue,
  )

  const { interestRateLabel } = useBorrowInterestRate()

  const { collateralPriceLabelWithUSD } = useCollateralPrice(
    collateralAmountValue,
  )

  const {
    totalLoanAmount,
    totalLoanAmountLabel,
    gasCompensation,
    issuanceFee,
    borrowingFeeRate,
  } = useLoanAmountWithFees()

  const { liquidationPriceLabel } = useLiquidationPrice(
    totalLoanAmount,
    collateralAmountValue,
  )

  const gasCompensationLabel = separateThousandsWithComma(
    bigIntToHumanReadableFormat(gasCompensation),
  )

  const onSubmit = useCallback(() => {
    handleBorrow()
    onClickContinue()
  }, [handleBorrow, onClickContinue])

  return (
    <>
      <BorrowProgress step={2} stepsCount={4} />
      <Block
        className={css({
          position: "relative",
          padding: theme.sizing.scale800,
          paddingTop: 0,
          paddingBottom: "170px",
          overflow: "hidden",
        })}
      >
        <Block
          className={css({
            flexDirection: "column",
            display: "flex",
            gap: theme.sizing.scale800,
            height: "100%",
            paddingBottom: theme.sizing.scale800,
            overflow: "scroll",
            scrollbarWidth: "none",
          })}
        >
          <BorrowReward />

          <BorrowReviewBox>
            <BorrowReviewListItem
              header="Collateral"
              description="Paid in BTC"
              endHeader={
                <>
                  {collateralAmount} <CurrencySymbol token="mBTC" />
                </>
              }
              endDescription={
                ENABLE_BORROW_LIQUIDATIONS_PRICES
                  ? `${collateralPriceLabelWithUSD}`
                  : undefined
              }
              hasBorder
            />
            <BorrowReviewListItem
              header="Collateralization ratio"
              description="Your collateral ratio determines liquidation risk. Higher ratios provide more safety."
              endHeader={`${collateralizationRatioLabel}%`}
              hasBorder
            />
            <BorrowReviewListItem
              header="Interest Rate (APR)"
              description="Simple interest, assessed annually."
              endHeader={`${interestRateLabel}%`}
            />
          </BorrowReviewBox>

          <BorrowReviewBox>
            {ENABLE_BORROW_LIQUIDATIONS_PRICES && (
              <BorrowReviewListItem
                header="Liquidation price"
                description="The BTC price at which your loan will be liquidated if collateral becomes insufficient."
                endHeader={`$${liquidationPriceLabel}`}
                hasBorder
              />
            )}
            <BorrowReviewListItem
              header="Liquidation fee deposit"
              description="The liquidation fee deposit is held against your collateral. If you get liquidated, it will go to the liquidator: otherwise, it's refunded when you close your loan."
              endHeader={`${gasCompensationLabel} MUSD`}
            />
          </BorrowReviewBox>

          <BorrowReviewBox backgroundColor={theme.colors.green50}>
            <BorrowReviewListItem
              header="Borrowed amount"
              endHeader={`${separateThousandsWithComma(loanAmount)} MUSD`}
              hasLightHeader
              hasBorder
            />
            <BorrowReviewListItem
              header="Liquidation fee deposit"
              endHeader={`${gasCompensationLabel} MUSD`}
              hasLightHeader
              hasBorder
            />
            <BorrowReviewListItem
              header={`Issuance fee ${bigIntToHumanReadableFormat(
                borrowingFeeRate * 100n,
              )}%`}
              endHeader={`${bigIntToHumanReadableFormat(
                issuanceFee,
                Number(MUSD.decimals),
                2,
              )} MUSD`}
              hasLightHeader
            />
            <BorrowReviewListItem
              header="Total loan amount"
              endHeader={`${totalLoanAmountLabel} MUSD`}
              margin={`${theme.sizing.scale800} 0 0`}
            />
          </BorrowReviewBox>
        </Block>
        <BorrowTerms
          onSubmit={onSubmit}
          label={`Borrow ${separateThousandsWithComma(loanAmount)} MUSD`}
          dataAttr="borrow-borrow_cta-step_3"
          hasAcceptedTerms={hasAcceptedTerms}
          setHasAcceptedTerms={setHasAcceptedTerms}
        />
      </Block>
    </>
  )
}
